import { type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence } from 'framer-motion';
import {
    EventFeedCard,
    EventFeedContainer,
    EventFeedList,
} from '@/features/EventFeed';
import { Reasoning, type ReasoningStep } from '@/components';
import { useOpenInNewWindow } from '@/hooks';
import { EngineService } from '@/services/engine';
import { useSourcesStore } from '@/stores';
import { getDetectionLabel } from '@/utils/detection-classes';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { ScoredEvent } from '@/types/event';
import { ThresholdDivider } from './ThresholdDivider';

interface SimulationFeedProps {
    events: ScoredEvent[];
    isLoading: boolean;
    confidenceThreshold?: number;
    isPreviewThreshold?: boolean;
    simulationSteps?: ReasoningStep[];
    alertRuleId?: string;
}

export function SimulationFeed({
    events,
    isLoading,
    confidenceThreshold,
    isPreviewThreshold = false,
    simulationSteps = [],
}: SimulationFeedProps) {
    const { t } = useTranslation(I18nNamespace.EVENTS);
    const getSourceById = useSourcesStore((state) => state.getSourceById);
    const { openEventInNewWindow } = useOpenInNewWindow();

    const sortedEvents = useMemo(
        () => [...events].sort((a, b) => b.confidence - a.confidence),
        [events],
    );

    const dividerPosition = useMemo(() => {
        if (confidenceThreshold === undefined || sortedEvents.length === 0) {
            return -1;
        }
        const idx = sortedEvents.findIndex(
            (e) => e.confidence < confidenceThreshold - 1,
        );
        if (idx === -1) {
            return sortedEvents.length;
        }
        return idx;
    }, [sortedEvents, confidenceThreshold]);

    const renderCard = (scored: ScoredEvent) => {
        const event = scored.event;
        const source = getSourceById(event.sourceId);
        const sourceType = event.sourceType ?? source?.type;
        return (
            <EventFeedCard
                key={event.id}
                thumbnailUrl={
                    event.thumbnailUrl ||
                    EngineService.getEventThumbnailUrl(event.id, true)
                }
                title={t('page.event')}
                description={getDetectionLabel(t, event.classId)}
                timestamp={event.createdAt}
                timeRange={
                    event.frames?.length
                        ? {
                              start: event.frames[0].time,
                              end: event.frames[event.frames.length - 1].time,
                          }
                        : undefined
                }
                cameraName={
                    event.sourceName ?? source?.displayName ?? undefined
                }
                sourceType={sourceType}
                confidence={scored.confidence}
                useRelativeTime
                onClick={() => openEventInNewWindow(event.id)}
                animated
            />
        );
    };

    const items: ReactNode[] = [];
    sortedEvents.forEach((scored, index) => {
        if (index === dividerPosition) {
            items.push(
                <ThresholdDivider
                    key="threshold-divider"
                    threshold={confidenceThreshold!}
                    isPreviewing={isPreviewThreshold}
                />,
            );
        }
        items.push(renderCard(scored));
    });
    if (dividerPosition === sortedEvents.length) {
        items.push(
            <ThresholdDivider
                key="threshold-divider"
                threshold={confidenceThreshold!}
                isPreviewing={isPreviewThreshold}
            />,
        );
    }

    const showReasoning = isLoading || simulationSteps.length > 0;
    const hasSimulated = events.length > 0 || showReasoning;

    if (!hasSimulated) return null;

    return (
        <div className="h-full">
            <EventFeedContainer
                title={t('sidebar.simulationResults')}
                isLoading={false}
                className="h-full"
            >
                {showReasoning && (
                    <Reasoning steps={simulationSteps} isThinking={isLoading} />
                )}
                <AnimatePresence mode="popLayout">
                    <EventFeedList>{items}</EventFeedList>
                </AnimatePresence>
            </EventFeedContainer>
        </div>
    );
}
